import { makeStyles } from '@mui/styles';
import Accordion from 'apollo-react/components/Accordion';
import AccordionDetails from 'apollo-react/components/AccordionDetails';
import AccordionSummary from 'apollo-react/components/AccordionSummary';
import Typography from 'apollo-react/components/Typography';
import StarSolid from 'apollo-react-icons/StarSolid';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getHighlightedText, getSearchResultObject, getSortedMenu } from './utils';
import { HIGHLIGHT_COLOR } from '../../../constants/CommonStylesConstants';
import { ENTITY_DISPLAY } from '../../../constants/Urls';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getFavorites } from '../../../form-designer/selectors/common.selectors';
import { getAvailableNavigation } from '../../../selectors/MasterSetupPageSelectors';
import Link from '../../Link';

const useStyles = makeStyles({
  drawerLink: {
    color: '#297dfd',
    fontSize: '16px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  drawerLinkContainer: {
    lineHeight: 1.5,
    padding: '0.5rem 0',
  },
  searchDrawerGroup: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    paddingTop: 0,
    paddingBottom: 0,
  },
  favoriteIcon: {
    marginRight: '10px',
    marginBottom: '-3px',
  },
  highlightedText: {
    backgroundColor: HIGHLIGHT_COLOR,
  },
  emptyResultsMessage: {
    marginTop: 10,
  },
  nonSubGroup: {
    marginLeft: -20,
  },
  overrideMinWidth: {
    minWidth: 'auto',
    '&.MuiAccordion-region': {
      minWidth: 'auto',
    },
    '&.MuiCollapse-root': {
      minWidth: 'auto',
    },
    '&.MuiPaper-root > div:nth-of-type(2)': {
      minWidth: 'auto',
    },
    '&.MuiCollapse-vertical': {
      minWidth: 'auto',
    },
    '&.MuiAccordion-root': {
      minWidth: 'auto',
    },
    '&.Mui-expanded': {
      minWidth: 'auto',
    },
    '&.Mui-expanded > div:nth-of-type(2)': {
      minWidth: 'auto',
    },
  },
});

const SearchDrawer = ({ drawer, searchText, renderGroupLink, closeDrawer }) => {
  const { data: recentEvents } = useSelector((state) => state.recentEvents);
  const navigation = useSelector(getAvailableNavigation);
  const favorites = useSelector(getFavorites);

  const classes = useStyles();
  const translateString = useTranslation();

  const sortedMenuElements = getSortedMenu({ navigation, drawerId: drawer.Id });

  const searchResultObject = useMemo(
    () => getSearchResultObject({ sortedMenuElements, searchText, recentEvents, favorites }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchText]
  );

  return searchResultObject.searchedElementsLength ? (
    searchResultObject.searchedElements?.map((drawer) => (
      <div key={drawer.Title}>
        {!!drawer.templateData?.length && (
          <Accordion className={classes.overrideMinWidth} defaultExpanded>
            <AccordionSummary className={classes.overrideMinWidth}>
              <Typography>
                {getHighlightedText(drawer.Title, searchText, classes.highlightedText)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              className={classNames(classes.overrideMinWidth, classes.searchDrawerGroup)}
            >
              {drawer.templateData.map((group, index) => (
                <Accordion
                  className={classes.overrideMinWidth}
                  key={drawer.Title + group.ID + index}
                  defaultExpanded
                >
                  {drawer.Title !== group.Text && (
                    <AccordionSummary className={classes.overrideMinWidth}>
                      <Typography>
                        {getHighlightedText(group.Text, searchText, classes.highlightedText)}
                      </Typography>
                    </AccordionSummary>
                  )}
                  <AccordionDetails className={classes.overrideMinWidth}>
                    <ul>
                      {drawer.Title !== 'Recent' && drawer.Title !== 'Favorites'
                        ? group.Links.map((link, index) => (
                            <li
                              key={link.ID + index}
                              ui-ta={link.ID}
                              className={classes.drawerLinkContainer}
                            >
                              {renderGroupLink({
                                ...link,
                                Text: getHighlightedText(
                                  link.Text,
                                  searchText,
                                  classes.highlightedText
                                ),
                              })}
                            </li>
                          ))
                        : drawer.Title === 'Recent'
                        ? group.events.map((event) => (
                            <li
                              key={event.EntityKey}
                              ui-ta={`Event${event.EntityKey}`}
                              className={classNames(
                                classes.drawerLinkContainer,
                                classes.nonSubGroup
                              )}
                            >
                              <Link
                                className={classes.drawerLink}
                                onClick={closeDrawer}
                                to={{
                                  pathname: ENTITY_DISPLAY,
                                  search: `${event.QueryString}`,
                                }}
                              >
                                {getHighlightedText(
                                  event.EntityKey,
                                  searchText,
                                  classes.highlightedText
                                )}
                              </Link>
                            </li>
                          ))
                        : group.favorites.map((favorite) => (
                            <li
                              className={classes.nonSubGroup}
                              key={favorite.EntityKey}
                              ui-ta={`Event${favorite.EntityKey}`}
                            >
                              <Link
                                className={classes.drawerLink}
                                onClick={closeDrawer}
                                to={{ pathname: ENTITY_DISPLAY, search: `${favorite.QueryString}` }}
                              >
                                <StarSolid className={classes.favoriteIcon} fontSize="small" />
                                {getHighlightedText(
                                  favorite.EntityKey,
                                  searchText,
                                  classes.highlightedText
                                )}
                              </Link>
                            </li>
                          ))}
                    </ul>
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>
        )}
      </div>
    ))
  ) : (
    <Typography className={classes.emptyResultsMessage}>
      {translateString('No data to display')}
    </Typography>
  );
};

export default SearchDrawer;
